/** Stage E3.1 — Real Chart panel (candlestick + volume + entry/stop/target markers).
 *
 * Uses recharts ComposedChart with a custom candle renderer. Not exchange-grade
 * but expressive enough for AI-assisted review + trade replay.
 */
import { useEffect, useState, useMemo } from "react";
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, Cell } from "recharts";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

export default function ChartPanel({ symbol, entry, stop, target, showReplay = false, entryDate, exitDate }) {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("1D");
  const [available, setAvailable] = useState(true);

  // Load the effective (per-symbol → default → app) preference for this symbol.
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    api.get(`/chart-preferences/resolve/${symbol}`)
      .then((r) => { if (!cancelled && r.data?.timeframe) setTimeframe(r.data.timeframe); })
      .catch(() => { /* fall back to the default 1D */ });
    return () => { cancelled = true; };
  }, [symbol]);

  // Persist a new timeframe per-symbol WITHOUT blocking chart interaction.
  const changeTimeframe = (tf) => {
    setTimeframe(tf);
    if (!symbol) return;
    api.put(`/chart-preferences/symbol/${symbol}`, { timeframe: tf })
      .catch(() => { /* silent — session pref stays applied even if save fails */ });
  };

  useEffect(() => {
    if (!symbol) return;
    // Guard against React StrictMode double-invoke — abort the first call so
    // only the second's response commits state; net effect is a single fetch.
    const ctrl = new AbortController();
    setLoading(true);
    api.get(`/market-intel/bars/${symbol}`, {
      params: { timeframe, limit: 260 }, signal: ctrl.signal,
    })
      .then(r => { setBars(r.data?.bars || []); setAvailable(!!r.data?.available); })
      .catch((e) => { if (e?.name !== "CanceledError") { setBars([]); setAvailable(false); } })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [symbol, timeframe]);

  const data = useMemo(() => bars.map(b => ({
    date: (b.t || "").slice(0, 10),
    o: b.o, h: b.h, l: b.l, c: b.c, v: b.v,
    range: [b.l, b.h],
    body: [Math.min(b.o, b.c), Math.max(b.o, b.c)],
    up: b.c >= b.o,
  })), [bars]);

  if (loading) return (
    <div className="h-64 flex items-center justify-center text-xs text-muted-foreground">
      <Loader2 size={12} className="animate-spin mr-2"/> Loading chart…
    </div>
  );

  if (!available || data.length === 0) return (
    <div className="border border-dashed border-border/40 p-6 text-center text-xs text-muted-foreground">
      Historical data unavailable for {symbol} — {showReplay ? "chart history could not be reconstructed for this trade." : "please try again shortly."}
    </div>
  );

  return (
    <div className="space-y-2" data-testid="chart-panel">
      <div className="flex items-center gap-2">
        <div className="label-cap">Chart · {symbol}</div>
        <div className="ml-auto flex items-center gap-1">
          {["1D", "1H"].map(tf => (
            <button
              key={tf}
              data-testid={`chart-tf-${tf}`}
              onClick={() => changeTimeframe(tf)}
              className={`mono text-[10px] px-1.5 py-0.5 uppercase ${timeframe === tf ? "text-foreground border-b border-current" : "text-muted-foreground hover:text-foreground"}`}
            >{tf}</button>
          ))}
        </div>
      </div>
      <div className="h-64 min-h-[256px] w-full border border-border/40" style={{ height: 256, minWidth: 320 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={256} minWidth={320} debounce={50}>
          <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
            <XAxis dataKey="date" hide/>
            <YAxis domain={[dataMin => dataMin * 0.98, dataMax => dataMax * 1.02]}
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} width={40}
                    tickFormatter={(v) => fmt(v, 1)}/>
            <Tooltip
              cursor={{ stroke: "var(--border)" }}
              contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 11, padding: "6px 8px" }}
              formatter={(v, k) => {
                if (k === "body" || k === "range") return null;
                if (k === "v") return [fmt(v, 0), "Vol"];
                return [`$${fmt(v)}`, k.toUpperCase()];
              }}
              labelFormatter={(l) => l}
            />
            {/* wick — thin dark bar between low & high */}
            <Bar dataKey="range" fill="var(--muted-foreground)" barSize={1} isAnimationActive={false}/>
            {/* body — thick red/green bar between open & close */}
            <Bar dataKey="body" barSize={5} isAnimationActive={false}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.up ? "#10b981" : "#ef4444"}/>
              ))}
            </Bar>
            {entry != null && <ReferenceLine y={entry} stroke="#3b82f6" strokeDasharray="4 4" label={{ value: `Entry ${fmt(entry)}`, fill: "#3b82f6", fontSize: 9, position: "insideRight" }}/>}
            {stop != null && <ReferenceLine y={stop} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `Stop ${fmt(stop)}`, fill: "#ef4444", fontSize: 9, position: "insideRight" }}/>}
            {target != null && <ReferenceLine y={target} stroke="#10b981" strokeDasharray="4 4" label={{ value: `Target ${fmt(target)}`, fill: "#10b981", fontSize: 9, position: "insideRight" }}/>}
            {showReplay && entryDate && (
              <ReferenceArea x1={entryDate.slice(0, 10)} x2={(exitDate || data[data.length - 1].date).slice(0, 10)}
                              fill="#3b82f6" fillOpacity={0.06}/>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
